import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import { VARIENT, SIZE } from "constant";
import debounce from "lodash.debounce";
import { TextInput } from "components/common/Input";
import { isMobile } from "components/globals";

SearchInput.propTypes = {
  onSearch: PropTypes.func.isRequired,
  showIcon: PropTypes.bool,
  isQuickSearch: PropTypes.bool, //if true - search after every key pressed. if false - search only after enter key pressed
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf([
    VARIENT.contained,
    VARIENT.outlined,
    VARIENT.empty,
  ]),
  size: PropTypes.oneOf([SIZE.small, SIZE.medium, SIZE.large, SIZE.xl]),
  rounded: PropTypes.bool,
  width: PropTypes.string,
  lineHeight: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.string,
  onClearInput: PropTypes.func,
  onKeyUp: PropTypes.func,
  minSearchLength: PropTypes.number,
  withDebounce: PropTypes.bool,
  maxCharactersLength: PropTypes.number,
  withBorder: PropTypes.bool,
};

SearchInput.defaultProps = {
  variant: VARIENT.outlined,
  rounded: false,
  lineHeight: true,
  autoFocus: false,
  minSearchLength: 1,
  size: SIZE.medium,
  withDebounce: true,
  withBorder: true,
};

const isMobileMode = isMobile();
const iconType = {
  magnify: "magnify_",
  remove: isMobileMode ? "x-default" : "x-small",
};
const FAST_TYPING_IN_MS = 500;

function SearchInput(props, ref) {
  const [searchIcon, setSearchIcon] = useState(iconType.magnify);
  const [placeholder, setPlaceholder] = useState(props.placeholder);
  const [value, setValue] = useState(props.value || "");

  useImperativeHandle(ref, () => ({
    onIconClick: () => {
      handleOnClickIcon();
    },
  }));

  useEffect(() => {
    initSearchIcon(props.value || "");
    handleResetSearch();
  }, [props.value]);

  const handleResetSearch = () => {
    !props.value && setValue(props.value);
  };

  const handleOnClickIcon = (element) => {
    if (searchIcon === iconType.remove) {
      setValue("");
      setSearchIcon(iconType.magnify);
      if (props.onClearInput) {
        props.onClearInput();
      }
    } else if (element) {
      element.focus();
    }
  };

  const handleOnChange = (value) => {
    if (isValidSearchLength(value.length)) {
      initSearchIcon(value);
      setValue(value);
      debouncedQuickSearch(value);
    }
  };

  const debouncedQuickSearch = useCallback(
    debounce(
      (value) => {
        if (props.onSearch && props.isQuickSearch) {
          props.onSearch(value);
        }
      },
      props.withDebounce ? FAST_TYPING_IN_MS : 0
    ),
    []
  );

  const handleOnKeyUp = (event) => {
    const searchValue = event.target.value;
    if (isValidSearchLength(searchValue.length + 1)) {
      if (event.keyCode === 13 && !props.isQuickSearch && props.onSearch) {
        if (searchValue.length >= props.minSearchLength) {
          props.onSearch(searchValue);
        } else if (searchValue.length !== 0) {
          setSearchIcon(iconType.magnify);
        } else {
          setSearchIcon(iconType.magnify);
          setPlaceholder(props.placeholder);
        }
      }
      if (props.onKeyUp) {
        props.onKeyUp(event);
      }
    }
  };

  const initSearchIcon = (value) => {
    if (value.length > 0) {
      setSearchIcon(iconType.remove);
    } else {
      setSearchIcon(iconType.magnify);
    }
  };

  const handleOnFocus = (event) => {
    if (props.onFocus && isValidSearchLength(value?.length)) {
      props.onFocus(event);
    }
  };

  const handleOnBlur = (value) => {
    if (props.onBlur && isValidSearchLength(value.length)) {
      props.onBlur(value);
    }
  };

  const handleOnKeyDown = (e) => {
    value && setSearchIcon(iconType.remove);
  };

  const isValidSearchLength = (newValueLength) => {
    return (
      !props.maxCharactersLength || newValueLength <= props.maxCharactersLength
    );
  };

  return (
    <TextInput
      placeholder={placeholder}
      variant={props.variant}
      rounded={props.rounded}
      width={props.width}
      value={value}
      iconName={props.showIcon ? searchIcon : null}
      lineHeight={props.lineHeight}
      autoFocus={props.autoFocus}
      onClickIcon={handleOnClickIcon}
      onChange={handleOnChange}
      onKeyUp={handleOnKeyUp}
      onKeyDown={handleOnKeyDown}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      size={props.size}
      withBorder={props.withBorder}
    ></TextInput>
  );
}

export default React.memo(forwardRef(SearchInput));
